import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Box,
  CircularProgress,
  TextField,
  Tooltip
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { WorkerAssignment } from '../types/workerAssignment';
import { usePermissions } from '../hooks/usePermissions';
import api from '../services/api';

interface ReceivesListDialogProps {
  open: boolean;
  onClose: () => void;
  assignment: WorkerAssignment | null;
  onReceiveDeleted: () => void;
}

interface EditFormData {
  receive_date: string;
  quantity_received: number;
  defect_quantity: number;
  notes: string;
}

const ReceivesListDialog: React.FC<ReceivesListDialogProps> = ({
  open,
  onClose,
  assignment,
  onReceiveDeleted
}) => {
  const { canEdit } = usePermissions();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [selectedReceiveId, setSelectedReceiveId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit state
  const [editingReceiveId, setEditingReceiveId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditFormData>({ receive_date: '', quantity_received: 0, defect_quantity: 0, notes: '' });
  const [isSaving, setIsSaving] = useState(false);

  const hasEditPermission = canEdit('assignments');

  const handleDeleteClick = (receiveId: number) => {
    setSelectedReceiveId(receiveId);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assignment || selectedReceiveId === null) return;

    setIsDeleting(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await api.delete(`/worker-assignments/${assignment.id}/receives/${selectedReceiveId}`);
      setSuccessMessage('Receive record deleted successfully');
      setDeleteConfirmOpen(false);
      onReceiveDeleted();
    } catch (err: any) {
      console.error('Error deleting receive:', err);
      setError(err.response?.data?.error || 'Failed to delete receive record');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = (receive: WorkerAssignment['receives'][0]) => {
    setEditingReceiveId(receive.id);
    setEditForm({
      receive_date: receive.receive_date.split('T')[0],
      quantity_received: receive.quantity_received,
      defect_quantity: receive.defect_quantity,
      notes: receive.notes || '',
    });
    setError(null);
    setSuccessMessage(null);
  };

  const handleEditCancel = () => {
    setEditingReceiveId(null);
    setError(null);
  };

  const handleEditSave = async () => {
    if (!assignment || editingReceiveId === null) return;

    // Client-side validation
    if (!editForm.receive_date) {
      setError('Receive date is required');
      return;
    }
    if (editForm.quantity_received < 1) {
      setError('Quantity received must be at least 1');
      return;
    }

    // Check total won't exceed issued
    const otherReceivesTotal = assignment.receives
      .filter(r => r.id !== editingReceiveId)
      .reduce((sum, r) => sum + r.quantity_received + r.defect_quantity, 0);

    const newTotal = otherReceivesTotal + editForm.quantity_received + editForm.defect_quantity;
    if (newTotal > assignment.quantity_issued) {
      setError(`Total received (${newTotal}) cannot exceed issued quantity (${assignment.quantity_issued})`);
      return;
    }

    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      await api.put(`/worker-assignments/${assignment.id}/receives/${editingReceiveId}`, {
        receive_date: editForm.receive_date,
        quantity_received: editForm.quantity_received,
        defect_quantity: editForm.defect_quantity,
        notes: editForm.notes,
      });
      setSuccessMessage('Receive record updated successfully');
      setEditingReceiveId(null);
      onReceiveDeleted(); // refresh data
    } catch (err: any) {
      console.error('Error editing receive:', err);
      setError(err.response?.data?.error || 'Failed to update receive record');
    } finally {
      setIsSaving(false);
    }
  };

  if (!assignment) return null;

  const showActions = hasEditPermission;

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Receives for Assignment #{assignment.id}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {successMessage && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {successMessage}
            </Alert>
          )}

          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              Design: {assignment.design_no} ({assignment.garment_part})
            </Typography>
            <Typography variant="subtitle1">
              Worker: {assignment.worker.name} ({assignment.worker_id})
            </Typography>
            <Typography variant="subtitle1">
              Issued: {assignment.quantity_issued} pieces
            </Typography>
          </Box>

          {assignment.receives.length === 0 ? (
            <Alert severity="info">No receive records found for this assignment</Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Quantity</TableCell>
                    <TableCell align="right">Defects</TableCell>
                    <TableCell>Notes</TableCell>
                    {showActions && <TableCell align="center">Actions</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {assignment.receives.map((receive) => (
                    <TableRow key={receive.id}>
                      {editingReceiveId === receive.id ? (
                        <>
                          <TableCell>
                            <TextField
                              type="date"
                              size="small"
                              value={editForm.receive_date}
                              onChange={(e) => setEditForm({ ...editForm, receive_date: e.target.value })}
                              sx={{ width: 150 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={editForm.quantity_received}
                              onChange={(e) => setEditForm({ ...editForm, quantity_received: Math.max(1, parseInt(e.target.value) || 0) })}
                              inputProps={{ min: 1 }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <TextField
                              type="number"
                              size="small"
                              value={editForm.defect_quantity}
                              onChange={(e) => setEditForm({ ...editForm, defect_quantity: Math.max(0, parseInt(e.target.value) || 0) })}
                              inputProps={{ min: 0 }}
                              sx={{ width: 80 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={editForm.notes}
                              onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                              placeholder="Notes"
                              sx={{ width: '100%', minWidth: 100 }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                              <Button
                                size="small"
                                variant="contained"
                                color="primary"
                                onClick={handleEditSave}
                                disabled={isSaving}
                              >
                                {isSaving ? <CircularProgress size={16} /> : 'Save'}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleEditCancel}
                                disabled={isSaving}
                              >
                                Cancel
                              </Button>
                            </Box>
                          </TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{new Date(receive.receive_date).toLocaleDateString()}</TableCell>
                          <TableCell align="right">{receive.quantity_received}</TableCell>
                          <TableCell align="right">{receive.defect_quantity}</TableCell>
                          <TableCell>{receive.notes || '-'}</TableCell>
                          {showActions && (
                            <TableCell align="center">
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title="Edit receive">
                                  <IconButton
                                    size="small"
                                    color="primary"
                                    onClick={() => handleEditClick(receive)}
                                  >
                                    <EditIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete receive">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => handleDeleteClick(receive.id)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          )}
                        </>
                      )}
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Total</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {assignment.receives.reduce((sum, r) => sum + r.quantity_received, 0)}
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 'bold' }}>
                      {assignment.receives.reduce((sum, r) => sum + r.defect_quantity, 0)}
                    </TableCell>
                    <TableCell colSpan={showActions ? 2 : 1}></TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this receive record? This action cannot be undone.
          </Typography>
          <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
            Note: You cannot delete receive records that are part of a monthly settlement.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={20} /> : null}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ReceivesListDialog;